type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

type CacheResult<T, A> = {
  current: (arg: A) => Promise<T>;
  invalidate: () => void;
};

export function useCache<T, A>(
  fetchFn: (arg: A) => Promise<T>,
  expiryTimeMs: number
): CacheResult<T, A> {
  const cacheMap = new Map<string, CacheEntry<T>>();
  const fetchPromises = new Map<string, Promise<T>>();

  const getCachedValue = async (arg: A): Promise<T> => {
    const cacheKey = JSON.stringify(arg);
    const now = Date.now();

    // If cache is valid, return cached value
    const cachedEntry = cacheMap.get(cacheKey);
    if (cachedEntry && now <= cachedEntry.expiresAt) {
      return cachedEntry.value;
    }

    // If there's already a fetch in progress for this key, return its promise
    const existingPromise = fetchPromises.get(cacheKey);
    if (existingPromise) return existingPromise;

    // Start new fetch
    const promise = fetchFn(arg);
    fetchPromises.set(cacheKey, promise);

    try {
      const freshValue = await promise;
      if (fetchPromises.get(cacheKey) === promise) {
        cacheMap.set(cacheKey, {
          value: freshValue,
          expiresAt: Date.now() + expiryTimeMs,
        });
      }

      return freshValue;
    } finally {
      if (fetchPromises.get(cacheKey) === promise)
        fetchPromises.delete(cacheKey);
    }
  };

  return {
    invalidate: () => {
      cacheMap.clear();
      fetchPromises.clear();
    },
    current: getCachedValue,
  };
}
